import React from 'react';
import { X, Sparkles, Loader2, CheckCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

/**
 * CV skill confirmation.
 *
 * Shown after a CV is attached, in the public job application, candidate
 * onboarding and My Details. Lists the skills found in the CV so the candidate can
 * remove anything wrong before it reaches their profile.
 *
 * Skills already on the profile are filtered out upstream (server-side, from the
 * session), so a candidate is never asked to re-confirm the same skill twice. When
 * nothing new is found this renders either nothing or a short reassurance line.
 *
 * Props:
 * - skills: [{ skill_id, name, category, occurrences }] currently kept
 * - loading: bool - extraction in progress
 * - error: string | null
 * - excludedCount: number - found but already on the profile
 * - onChange(nextSkills) - called when the candidate removes one or clears all
 * - variant: 'inline' | 'card' (default 'card')
 */
const CVSkillConfirmation = ({
  skills = [],
  loading = false,
  error = null,
  excludedCount = 0,
  onChange,
  variant = 'card',
}) => {
  const removeSkill = (skillId) => {
    onChange?.(skills.filter((s) => s.skill_id !== skillId));
  };

  const clearAll = () => onChange?.([]);

  let body;

  if (loading) {
    body = (
      <div className="flex items-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Reading your CV for skills...
      </div>
    );
  } else if (error) {
    // Extraction is a convenience, never a blocker - say so and move on
    body = (
      <p className="text-sm text-muted-foreground">
        We couldn't read skills from your CV automatically. That's fine, you can add
        them to your profile later.
      </p>
    );
  } else if (skills.length === 0 && excludedCount > 0) {
    body = (
      <div className="flex items-start text-sm text-muted-foreground">
        <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-600 shrink-0" />
        <span>
          We read your CV: all {excludedCount} skill{excludedCount === 1 ? '' : 's'} we
          found {excludedCount === 1 ? 'is' : 'are'} already on your profile. Nothing to do.
        </span>
      </div>
    );
  } else if (skills.length === 0) {
    // Nothing found and nothing already held - stay silent rather than show an
    // empty box that reads like a failure
    return null;
  } else {
    body = (
      <>
        <div className="flex items-start justify-between gap-4 mb-3">
          <p className="text-sm text-muted-foreground">
            We think we found these skills in your CV. Remove any that aren't correct.
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            Clear all
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {skills.map((skill) => (
            <Badge
              key={skill.skill_id}
              className="bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200 pl-3 pr-1 py-1 flex items-center gap-1"
            >
              <span>{skill.name}</span>
              <button
                type="button"
                onClick={() => removeSkill(skill.skill_id)}
                aria-label={`Remove ${skill.name}`}
                className="rounded-full p-0.5 hover:bg-blue-300/60 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>

        {excludedCount > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            {excludedCount} other skill{excludedCount === 1 ? '' : 's'} we found
            {excludedCount === 1 ? ' is' : ' are'} already on your profile.
          </p>
        )}
      </>
    );
  }

  const heading = (
    <h3 className="font-medium text-sm flex items-center mb-1">
      <Sparkles className="h-4 w-4 mr-2 text-blue-600" />
      Skills from your CV
    </h3>
  );

  if (variant === 'inline') {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
        {heading}
        {body}
      </div>
    );
  }

  return (
    <Card className="border-blue-200 bg-blue-50/50">
      <CardContent className="pt-6">
        {heading}
        {body}
      </CardContent>
    </Card>
  );
};

export default CVSkillConfirmation;
